// Registros de una colección desde la réplica local-first, y escrituras que
// pasan por su outbox. Si la colección no está «Disponible sin conexión» (o no
// hay motor), `active` es false y las vistas usan sus queries y mutations de
// siempre (docs/diseno-libreria-local-first.md §6.4).

import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useMutation, useQuery } from "convex/react";
import { useEngine, useLocalQuery, useScopeStatus } from "@heskala/localfirst/react";
import { SyncRejectedError, type MutationCall, type OpDraft, type ViewRow } from "@heskala/localfirst/core";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import { collectionScope, findRow, toRecord, type LocalRecord } from "@/lib/localfirst/records";

export interface CollectionReplica {
  /** La colección se lee y escribe por la réplica local. */
  active: boolean;
  scope: string | null;
  status: ReturnType<typeof useScopeStatus>;
  records: LocalRecord[];
  /** Sin nada local todavía (primera descarga). */
  hydrating: boolean;
  readOnly: boolean;
}

export function useCollectionReplica(collectionId: string | null | undefined): CollectionReplica {
  const engine = useEngine();
  const scope = collectionId ? collectionScope(collectionId) : null;
  const status = useScopeStatus(scope);
  const active = !!engine && !!status && status.status !== "disabled" && status.status !== "denied";
  const { rows } = useLocalQuery(active ? scope : null);
  const records = useMemo(() => rows.map((row) => toRecord(row, collectionId ?? undefined)), [rows, collectionId]);
  return {
    active,
    scope,
    status,
    records,
    hydrating: active && rows.length === 0 && status?.status === "hydrating",
    readOnly: !!status?.readOnly,
  };
}

/** Un registro de la réplica por `_id` o uuid. */
export function useReplicaRecord(collectionId: string | null | undefined, id: string | null | undefined) {
  const replica = useCollectionReplica(collectionId);
  const record = useMemo(() => {
    if (!replica.active || !id) return undefined;
    return replica.records.find((r) => r._id === id || r.uuid === id) ?? null;
  }, [replica.active, replica.records, id]);
  return { ...replica, record };
}

/**
 * Con red espera al servidor (el error se muestra donde se hizo el cambio y no
 * va a la bandeja); sin red, basta con que quede guardado en el dispositivo.
 */
async function settle(engine: NonNullable<ReturnType<typeof useEngine>>, call: MutationCall): Promise<void> {
  await call.local;
  if (!engine.syncStatus().online) return;
  try {
    await call.server;
  } catch (error) {
    if (error instanceof SyncRejectedError) await engine.discardRecovery(call.groupId);
    throw error;
  }
}

/** Diferencia entre dos juegos de valores como `set` de una operación (null vacía). */
export function valuesPatch(before: Record<string, unknown>, after: Record<string, unknown>): Record<string, unknown> {
  const set: Record<string, unknown> = {};
  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    if (key.startsWith("_")) continue;
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) set[key] = after[key] ?? null;
  }
  return set;
}

export interface RecordWriter {
  active: boolean;
  /** Sin conexión en este momento (lo escrito queda en el dispositivo). */
  offline(): boolean;
  create(values: Record<string, unknown>): Promise<string>;
  /** `values` reemplaza los valores (como `updateCollectionRecord`). */
  update(id: string, values: Record<string, unknown>): Promise<void>;
  /** Solo los campos indicados (como `patchCollectionRecordValues`). */
  patch(id: string, values: Record<string, unknown>): Promise<void>;
  remove(id: string): Promise<void>;
  /**
   * Documento con líneas: el padre y los cambios de sus líneas en un grupo
   * atómico (el servidor lo guarda con el guardado de documentos). Las líneas
   * van completas y en orden, como en `saveCollectionDocument`.
   */
  saveDocument(input: DocumentInput): Promise<DocumentSaved>;
}

export interface DocumentInput {
  recordId?: string;
  values: Record<string, unknown>;
  children: Array<{
    collectionId: string;
    parentFieldKey: string;
    lines: Array<{ id?: string; values: Record<string, unknown> }>;
  }>;
}

export interface DocumentSaved {
  id: string;
  /** Id de cada línea enviada, en orden, por colección de líneas. */
  lines: Record<string, string[]>;
  created: number;
  updated: number;
  removed: number;
}

/** ¿La fila es una línea de ese documento? (por la relación o por `parentId`). */
export function isLineOf(row: ViewRow, parent: Pick<ViewRow, "id" | "docId">, parentFieldKey: string): boolean {
  const ref = row.values[parentFieldKey];
  const parentId = (row.meta as { parentId?: string } | undefined)?.parentId;
  return ref === parent.id || (!!parent.docId && (ref === parent.docId || parentId === parent.docId));
}

const withoutInternal = (values: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(values).filter(([k, v]) => !k.startsWith("_") && v !== undefined));

/**
 * Escrituras de registros: por la réplica si la colección está disponible sin
 * conexión, por las mutations de siempre si no.
 */
export function useRecordWriter(collectionId: string | null | undefined): RecordWriter {
  const engine = useEngine();
  const replica = useCollectionReplica(collectionId);
  const createRecord = useMutation(api.companies.collections.mutations.createCollectionRecord);
  const updateRecord = useMutation(api.companies.collections.mutations.updateCollectionRecord);
  const patchValues = useMutation(api.companies.collections.mutations.patchCollectionRecordValues);
  const deleteRecord = useMutation(api.companies.collections.mutations.deleteCollectionRecord);
  const saveDocument = useMutation(api.companies.collections.documents.saveCollectionDocument);
  // Solo lectura sin conexión: con red se escribe por las mutations de
  // siempre; sin red, no se puede.
  const local = replica.active && !replica.readOnly && !!engine && !!replica.scope;
  const blocked = replica.active && replica.readOnly && !!engine && !engine.syncStatus().online;

  const rowOf = useCallback(
    (id: string): ViewRow => {
      const row = findRow(engine!.rows(replica.scope!), id);
      if (!row) throw new Error("El registro ya no está en este dispositivo");
      return row;
    },
    [engine, replica.scope],
  );

  return useMemo<RecordWriter>(() => {
    if (blocked) {
      const fail = async (): Promise<never> => {
        throw new Error("Sin conexión: esta colección es solo de lectura en este dispositivo");
      };
      return { active: false, offline: () => true, create: fail, update: fail, patch: fail, remove: fail, saveDocument: fail };
    }
    if (!local) {
      return {
        active: false,
        offline: () => false,
        create: async (values) =>
          String(await createRecord({ collectionId: collectionId as Id<"collections">, values: values as never })),
        update: async (id, values) => {
          await updateRecord({ id: id as Id<"collection_records">, values: values as never });
        },
        patch: async (id, values) => {
          await patchValues({ id: id as Id<"collection_records">, values: values as never });
        },
        remove: async (id) => {
          await deleteRecord({ id: id as Id<"collection_records"> });
        },
        saveDocument: async ({ recordId, values, children }) => {
          const result = await saveDocument({
            collectionId: collectionId as Id<"collections">,
            ...(recordId ? { id: recordId as Id<"collection_records"> } : {}),
            values: values as never,
            children: children.map((c) => ({
              collectionId: c.collectionId as Id<"collections">,
              lines: c.lines.map((l) => ({ ...(l.id ? { id: l.id as Id<"collection_records"> } : {}), values: l.values as never })),
            })),
          });
          return { ...result, id: String(result.id), lines: result.lines as Record<string, string[]> };
        },
      };
    }
    const scope = replica.scope!;
    return {
      active: true,
      offline: () => !engine!.syncStatus().online,
      create: async (values) => {
        const set = Object.fromEntries(Object.entries(values).filter(([k, v]) => !k.startsWith("_") && v !== undefined));
        const call = engine!.commit([{ scope, kind: "create", set }]);
        await settle(engine!, call);
        const rowId = call.rowIds[0]!;
        return engine!.row(scope, rowId)?.docId ?? rowId;
      },
      update: async (id, values) => {
        const row = rowOf(id);
        const set = valuesPatch(row.values, values);
        if (Object.keys(set).length === 0) return;
        await settle(engine!, engine!.commit([{ scope, kind: "patch", rowId: row.id, set }]));
      },
      patch: async (id, values) => {
        const row = rowOf(id);
        await settle(engine!, engine!.commit([{ scope, kind: "patch", rowId: row.id, set: { ...values } }]));
      },
      remove: async (id) => {
        const row = rowOf(id);
        await settle(engine!, engine!.commit([{ scope, kind: "delete", rowId: row.id }]));
      },
      saveDocument: async ({ recordId, values, children }) => {
        const parent = recordId ? rowOf(recordId) : undefined;
        const parentRowId = parent?.id ?? crypto.randomUUID();
        // Línea nueva de un documento nuevo: apunta al uuid del documento (el
        // servidor la traduce al `_id`).
        const parentRef = parent?.docId ?? parentRowId;
        const drafts: OpDraft[] = [
          parent
            ? { scope, kind: "patch", rowId: parent.id, set: valuesPatch(parent.values, values) }
            : { scope, kind: "create", rowId: parentRowId, set: withoutInternal(values) },
        ];
        const lines: Record<string, string[]> = {};
        let created = 0;
        let updated = 0;
        let removed = 0;
        for (const child of children) {
          const childScope = collectionScope(child.collectionId);
          const state = engine!.scopeState(childScope);
          if (!state || state.status === "denied" || state.status === "disabled") {
            throw new Error("Las líneas de este documento todavía no están en este dispositivo");
          }
          const rows = engine!.rows(childScope);
          const current = parent ? rows.filter((r) => isLineOf(r, parent, child.parentFieldKey)) : [];
          const kept = new Set<string>();
          const ids: string[] = [];
          for (const line of child.lines) {
            const row = line.id ? findRow(rows, line.id) : undefined;
            if (row) {
              kept.add(row.id);
              ids.push(row.docId ?? row.id);
              const set = valuesPatch(row.values, { ...line.values, [child.parentFieldKey]: row.values[child.parentFieldKey] });
              if (Object.keys(set).length > 0) {
                drafts.push({ scope: childScope, kind: "patch", rowId: row.id, set });
                updated += 1;
              }
            } else {
              const rowId = crypto.randomUUID();
              ids.push(rowId);
              drafts.push({
                scope: childScope,
                kind: "create",
                rowId,
                set: { ...withoutInternal(line.values), [child.parentFieldKey]: parentRef },
              });
              created += 1;
            }
          }
          for (const row of current) {
            if (kept.has(row.id)) continue;
            drafts.push({ scope: childScope, kind: "delete", rowId: row.id });
            removed += 1;
          }
          lines[child.collectionId] = ids;
        }
        const nothing = parent && drafts.length === 1 && Object.keys(drafts[0]!.set ?? {}).length === 0;
        if (!nothing) await settle(engine!, engine!.commit(drafts));
        return { id: engine!.row(scope, parentRowId)?.docId ?? parentRef, lines, created, updated, removed };
      },
    };
  }, [local, blocked, engine, replica.scope, rowOf, collectionId, createRecord, updateRecord, patchValues, deleteRecord, saveDocument]);
}

/**
 * Un registro para el formulario: de la réplica si la colección está
 * disponible sin conexión, de `getCollectionRecord` si no. `undefined`
 * mientras carga; `null` si no existe.
 */
export function useCollectionRecordDoc(collectionId: string | null | undefined, id: string | null | undefined) {
  const replica = useReplicaRecord(collectionId, id);
  const live = useQuery(
    api.companies.collections.queries.getCollectionRecord,
    id && !replica.active ? { id } : "skip",
  );
  if (!id) return undefined;
  if (replica.active) {
    if (replica.record) return replica.record as unknown as NonNullable<typeof live>;
    return replica.hydrating ? undefined : null;
  }
  return live;
}

/**
 * Líneas de un documento desde la réplica, con la forma de `getDocumentLines`.
 * `undefined` si alguna colección de líneas no está en el dispositivo (el
 * formulario usa entonces la consulta del servidor).
 */
export function useLocalDocumentLines(
  record: { _id: string; uuid?: string } | null | undefined,
  children: ReadonlyArray<{ collectionId: string; parentFieldKey: string; orderByFieldKey?: string }>,
) {
  const engine = useEngine();
  const version = useSyncExternalStore(
    useCallback((listener: () => void) => (engine ? engine.subscribe(listener) : () => {}), [engine]),
    () => engine?.getVersion() ?? 0,
    () => engine?.getVersion() ?? 0,
  );
  return useMemo(() => {
    void version;
    if (!engine || !record || children.length === 0) return undefined;
    const out: Array<{ collectionId: string; lines: LocalRecord[]; relationLabels: Record<string, unknown> }> = [];
    for (const child of children) {
      const scope = collectionScope(child.collectionId);
      const state = engine.scopeState(scope);
      if (!state || state.status === "denied" || state.status === "disabled") return undefined;
      const parent = { id: record.uuid ?? record._id, docId: record._id };
      const order = (r: LocalRecord) => {
        const position = child.orderByFieldKey ? r.values[child.orderByFieldKey] : undefined;
        return typeof position === "number" ? position : r._creationTime;
      };
      const lines = engine
        .rows(scope)
        .filter((r) => isLineOf(r, parent, child.parentFieldKey))
        .map((r) => toRecord(r, child.collectionId))
        .sort((a, b) => order(a) - order(b));
      const relationLabels: Record<string, unknown> = {};
      for (const line of lines) Object.assign(relationLabels, (line._relations as { labels?: Record<string, unknown> } | undefined)?.labels ?? {});
      out.push({ collectionId: child.collectionId, lines, relationLabels });
    }
    return out;
  }, [engine, record, children, version]);
}
